/**
 * persistence/managers/DiffManager.js
 *
 * Computes diffs between two event strings and appends diff records to
 * IndexedDB.  Designed to be fast on small strings (< 50KB).
 *
 * Event strings are comma-separated tokens like:
 *   "P-A-B-1H-1-D-D-A-1-G-X-X,S-A-B-1H-2-..."
 *
 * Analysts mostly append new events (pass, shot, duel...) — they rarely
 * delete or reorder existing ones.  We exploit this with a fast "append
 * detection" path that is O(1) for pure appends.  Full set-diff is only
 * needed when removals are detected (editing/correction mode).
 */

import { diffLogTable } from "../db/database";
import { EMPTY_EVENT_TOKEN, DIFF_LOG_MAX_PER_SESSION } from "../config";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Splits a raw event string into a cleaned array of meaningful event tokens.
 * Filters out empty tokens, whitespace, and the sentinel empty-event token.
 *
 * @param {string} rawString
 * @returns {string[]}
 */
export function parseEventTokens(rawString) {
  if (!rawString) return [];
  return rawString
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t && t !== EMPTY_EVENT_TOKEN);
}

/**
 * Counts meaningful events in a raw event string.
 * Used everywhere we need an event count without computing a full diff.
 *
 * @param {string} rawString
 * @returns {number}
 */
export function countEvents(rawString) {
  return parseEventTokens(rawString).length;
}

// ─── Diff computation ─────────────────────────────────────────────────────────

/**
 * Computes the diff between two event strings.
 *
 * Fast path (O(1)):
 *   If next is a pure extension of prev (all prev tokens are a prefix of next),
 *   only the appended slice is returned as addedEvents.
 *
 * Full path (O(n)):
 *   When tokens were removed or reordered, we do a full set-difference.
 *   Since strings are < 50KB this is still imperceptible.
 *
 * @param {string} prevString  Previous event string from Redux
 * @param {string} nextString  New event string from Redux
 * @returns {{
 *   addedEvents: string[],
 *   removedEvents: string[],
 *   prevEventCount: number,
 *   newEventCount: number,
 *   hasChange: boolean,
 * }}
 */
export function computeDiff(prevString, nextString) {
  const prevTokens = parseEventTokens(prevString);
  const nextTokens = parseEventTokens(nextString);

  const prevEventCount = prevTokens.length;
  const newEventCount  = nextTokens.length;

  // Guard: no meaningful change
  if (prevString === nextString) {
    return {
      addedEvents: [],
      removedEvents: [],
      prevEventCount,
      newEventCount,
      hasChange: false,
    };
  }

  // ── Fast path: pure append ──────────────────────────────────────────────────
  // Check if all previous tokens are still present at the start of the new list
  const isPureAppend =
    nextTokens.length >= prevTokens.length &&
    prevTokens.every((token, i) => nextTokens[i] === token);

  if (isPureAppend) {
    const addedEvents = nextTokens.slice(prevTokens.length);
    return {
      addedEvents,
      removedEvents: [],
      prevEventCount,
      newEventCount,
      hasChange: addedEvents.length > 0,
    };
  }

  // ── Full path: removals detected ────────────────────────────────────────────
  const prevSet = new Set(prevTokens);
  const nextSet = new Set(nextTokens);

  const addedEvents   = nextTokens.filter((t) => !prevSet.has(t));
  const removedEvents = prevTokens.filter((t) => !nextSet.has(t));

  return {
    addedEvents,
    removedEvents,
    prevEventCount,
    newEventCount,
    hasChange: addedEvents.length > 0 || removedEvents.length > 0,
  };
}

// ─── IndexedDB writes ─────────────────────────────────────────────────────────

/**
 * Appends a diff record to the diffLog table.
 * Fire-and-forget — never awaited in the hot Redux path.
 *
 * @param {object} params
 * @param {string}   params.matchId
 * @param {string}   params.analystId
 * @param {object}   params.diff        Result of computeDiff()
 * @param {number|null} params.baseVersionId  Most recent versions.id, or null
 */
export async function appendDiffLog({ matchId, analystId, diff, baseVersionId = null }) {
  if (!diff.hasChange) return;

  try {
    await diffLogTable().add({
      matchId,
      analystId,
      timestamp: Date.now(),
      prevEventCount: diff.prevEventCount,
      newEventCount:  diff.newEventCount,
      addedEvents:    diff.addedEvents,
      removedEvents:  diff.removedEvents,
      baseVersionId,
    });
  } catch (error) {
    // Non-fatal — history is best-effort
    console.warn("[DiffManager] appendDiffLog failed:", error.message);
  }
}

/**
 * Prunes diffLog entries for a session that exceed DIFF_LOG_MAX_PER_SESSION.
 * Deletes the oldest entries first.  Called on session start, not in the
 * hot path.
 *
 * @param {string} matchId
 * @param {string} analystId
 */
export async function pruneDiffLog(matchId, analystId) {
  try {
    const all = await diffLogTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .sortBy("timestamp");

    const excess = all.length - DIFF_LOG_MAX_PER_SESSION;
    if (excess > 0) {
      const idsToDelete = all.slice(0, excess).map((r) => r.id);
      await diffLogTable().bulkDelete(idsToDelete);
    }
  } catch (error) {
    console.warn("[DiffManager] pruneDiffLog failed:", error.message);
  }
}

/**
 * Returns all diffLog records for a session, ordered oldest-first.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<object[]>}
 */
export async function getDiffLog(matchId, analystId) {
  try {
    return await diffLogTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .sortBy("timestamp");
  } catch (error) {
    console.warn("[DiffManager] getDiffLog failed:", error.message);
    return [];
  }
}
